import type { ReactNode } from "react";
import { Badge, fmtLongDate, type PortalDashboard } from "@dearbill/shared";
import { plusOneMonth } from "../dashboardFormat";
import { m3pBadge } from "../m3pStatusBadge";

const HIGHLIGHT =
  "No interest, no fees: Your drug cost burden is spread evenly over all remaining months of the year.";

/**
 * The M3P program card on My Programs: the title and subtitle, the
 * plan-enrollment status badge, and three details. Enrollment Date is the
 * plan enrollment's effective date, Covered Medications the enrollment's
 * prescriptions minus the ones the assigned plan excludes, and Next Review
 * Date the month after the savings refresh, as on Home. The badge is
 * `m3pBadge`, as on Home. The highlight line renders only
 * while the status is confirmed or active.
 */
export function M3pProgramCard({ data }: { data: PortalDashboard }): ReactNode {
  const enrollment = data.latestEnrollment;
  const status = enrollment?.m3pStatus ?? null;
  const { label, tone } = m3pBadge(enrollment);
  const excluded = data.m3p?.excludedRxcuis ?? [];
  const covered = (enrollment?.prescriptionsList ?? [])
    .filter((p) => !excluded.includes(p.rxcui))
    .map((p) => p.drugName)
    .join(", ");
  const details: [string, string][] = [
    ["Enrollment Date", fmtLongDate(enrollment?.m3pEffectiveDate)],
    ["Covered Medications", covered || "—"],
    ["Next Review Date", fmtLongDate(plusOneMonth(data.savingsLastUpdatedAt))],
  ];
  return (
    <section className="border-haze flex flex-col gap-5 rounded-2xl border bg-white p-7">
      <div className="flex items-center justify-between gap-4">
        <div className="flex flex-col gap-1">
          <h3 className="text-body-lg text-midnight font-bold">
            Medicare Prescription Payment Plan (M3P)
          </h3>
          <p className="text-body-sm text-slate-teal">Standard Federal Medicare Part D Benefit</p>
        </div>
        <Badge tone={tone} className="shrink-0">
          {label}
        </Badge>
      </div>
      <dl className="grid gap-10 lg:grid-cols-3">
        {details.map(([term, value]) => (
          <div key={term} className="flex flex-col gap-1.5">
            <dt className="text-body-sm text-slate-teal">{term}</dt>
            <dd className="text-body-sm text-midnight font-semibold">{value}</dd>
          </div>
        ))}
      </dl>
      {(status === "confirmed" || status === "active") && (
        <p className="bg-success-bg text-body-sm text-forest rounded-lg px-3 py-3 font-medium">
          {HIGHLIGHT}
        </p>
      )}
    </section>
  );
}
